import React, { useEffect, useState, useRef } from 'react';
import SunEditor from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css"; // Import Sun Editor's CSS File
import 'katex/dist/katex.min.css';
import { Button, FormControl, InputLabel, MenuItem, Select} from '@material-ui/core';

import { EditorialTemplate, EditorialType } from '../../domain/domain';
import { getEditorialTemplates } from '../../api/editorial';
import { createHeaderPlugin } from './headerPlugin';
import { createLevelsPlugin } from './levelsPlugin';
import { createSymbolsTagsPlugin } from './symbolsTagsPlugin';
import { createTableSettingsDialogPlugin } from './tableSettingsDialogPlugin';
import { createTemplateEditorPlugin } from './templateEditorPlugin';

import './style.css';

const katex = require('katex');

type Props = {
    showToolbar: boolean,
    html: string,
    readOnly: boolean,
    editorStyle: any,
    showEditorialTemplates?: boolean,
    showEditorialTypes?: boolean,
    showEditorialButtons?: boolean,
    selectedEditorialType?: EditorialType,
    onChange: (newHtml: string) => void,
}

type WrapperProps = {
    showToolbar: boolean,
    html: string,
    readOnly: boolean,
    showEditorialTemplates?: boolean,
    showEditorialTypes?: boolean,
    showEditorialButtons?: boolean,
    selectedEditorialType?: EditorialType,
    onChange: (newHtml: string) => void,
    templates: EditorialTemplate[],
    showResizingBar?: boolean
}

const SunEditorWrapper: React.FC<WrapperProps> = ({ showToolbar, readOnly, html, onChange, templates, showResizingBar = true }) => {
    const [isContextMenuVisible, setIsContextMenuVisible] = useState(false);
    const [selectedCells, setSelectedCells] = useState<HTMLElement[]>([]);
    const editorRef = useRef<any>(null);
    const contextMenuRef = useRef<HTMLDivElement>(null);

    let header = createHeaderPlugin();
    let templateEditor = createTemplateEditorPlugin(templates);
    let tableSettingsDialog = createTableSettingsDialogPlugin();
    let symbolsTags = createSymbolsTagsPlugin();
    let levels = createLevelsPlugin();

    let buttonsList = [
        ["undo", "redo"],
        ['levels'],
        [
            "paragraphStyle",
            "bold",
            "underline",
            "italic",
            "strike",
            "subscript",
            "superscript",
        ],
        ["math"],
        ["horizontalRule"],
        ["header"],
        ["align", "list"],
        ["outdent", "indent"],
        ["table", "tableSettingsDialog"],
        ["symbolsTags"]
    ];

    if (templates.length > 0) {
        buttonsList.push(["template_editor"]);
    }

    const handleSelectionChange = () => {
        const editor = editorRef.current?.editor;
        if (!editor) return;

        // Detect multiple cell selections
        const selectedElements = Array.from(editor.getContext().element.wysiwyg.querySelectorAll('.se-table-selected-cell') || []);
        if (selectedElements.length > 1) {
            setSelectedCells(selectedElements as HTMLElement[]);
            setIsContextMenuVisible(true);
            selectedElements.forEach((cell: any) => {
                cell.classList.add('multiple-selected-cell');
            });
            const contextMenu = contextMenuRef.current;
            if (contextMenu) {
                const firstSelectedElement = selectedElements[selectedElements.length - 1];

                // Runtime check to ensure the element is an HTMLElement
                if (firstSelectedElement instanceof HTMLElement) {
                    contextMenu.style.top = `${firstSelectedElement.getBoundingClientRect().top + window.scrollY - 120}px`;
                    contextMenu.style.left = `${firstSelectedElement.getBoundingClientRect().left + window.scrollX - 50}px`;
                    contextMenu.style.display = 'block';
                }
            }
        } else {
            const selectedCells = document.querySelectorAll('.multiple-selected-cell');

            selectedCells.forEach((cell) => {
                cell.classList.remove('multiple-selected-cell');
            });
            setIsContextMenuVisible(false);
        }
    };

    const handleAlignmentChange = (alignment: string) => {
        const getAlignmentValues = (combo: string): { textAlign: string; verticalAlign: string } => {
            const [vertical, horizontal] = combo.split('-');
            const verticalAlign = { top: 'top', middle: 'middle', bottom: 'bottom' }[vertical] || 'top';
            const textAlign = { left: 'left', center: 'center', right: 'right' }[horizontal] || 'left';
            return { textAlign, verticalAlign };
        };

        const { textAlign, verticalAlign } = getAlignmentValues(alignment);

        selectedCells.forEach(cell => {
            const div = cell.querySelector('div');
            if (!div) return;

            div.setAttribute('data-cell-align', alignment);
            div.style.textAlign = textAlign;
            cell.style.verticalAlign = verticalAlign;
        });

        setIsContextMenuVisible(false);

        const editorInstance = editorRef.current?.editor;
        editorInstance?.core.history.push(true);
    };

    const handleFormattingOption = (option: string) => {
        const editorInstance = editorRef.current?.editor;
        if (!editorInstance) return;

        selectedCells.forEach(cell => {
            // Find the div inside the cell
            const div = cell.querySelector('div');
            if (!div) return;

            let content = div.innerHTML.trim();

            switch (option) {
                case 'bold':
                    // <strong> is used due to Suneditor requirement.
                    // EditorPanel.tsx functions parseHtmlForEditor & replaceStrongWithBold handle <strong>/<b> conversion.
                    content = content.includes('<strong>')
                        ? content.replace(/<strong>(.*?)<\/strong>/g, '$1')
                        : `<strong>${content}</strong>`;
                    break;
                case 'italic':
                    content = content.includes('<em>')
                        ? content.replace(/<em>(.*?)<\/em>/g, '$1')
                        : `<em>${content}</em>`;
                    break;
                default:
                    break;
            }
            div.innerHTML = content;
        });

        selectedCells.forEach((cell) => {
            cell.classList.remove('multiple-selected-cell');
        });

        editorInstance.core.history.push(false);
        setIsContextMenuVisible(false);
    };

    useEffect(() => {
        const buttons = [
            { selector: 'button[title="Solid"]', newTitle: 'Bleed Rule' },
            { selector: 'button[title="Dotted"]', newTitle: 'Single Horizontal' },
            { selector: 'button[title="Dashed"]', newTitle: 'Double Horizontal' }
        ];

        buttons.forEach(button => {
            const element = document.querySelector(button.selector);
            if (element) {
                element.setAttribute('title', button.newTitle);
            }
        });

        document.addEventListener('selectionchange', handleSelectionChange);

        return () => {
            document.removeEventListener('selectionchange', handleSelectionChange);
        };
    }, [selectedCells]);

    return (
        <div>
            <SunEditor
                ref={editorRef}
                defaultValue={html}
                setContents={html}
                onChange={onChange}
                showToolbar={showToolbar}
                disable={readOnly}
                setOptions={{
                    buttonList: buttonsList,
                    customPlugins: [
                        header,
                        templateEditor,
                        tableSettingsDialog,
                        symbolsTags,
                        levels
                    ],
                    minHeight: "300px",
                    resizingBar: showResizingBar,
                    paragraphStyles: [
                        {
                            name: "Double Indent",
                            class: "__se__p-double-indent",
                        },
                    ],
                    katex: {
                        src: katex,
                        options: {
                            output: "mathml"
                        },
                    },
                    showPathLabel: false,
                    tabDisable: false,
                    addTagsWhitelist: "L1|L2|L3|L4|L5|L6|HEADING|T|t|PL|BL|DL|BL_1",
                    pasteTagsBlacklist: "div|span",
                    attributesWhitelist: { all: "data-cell-align" },
                    // Using a custom save handler, so we disable SunEditor's internal save shortcut to ensure Cmd + S exclusively triggers our custom handler.
                    shortcutsDisable: ['save'],
                }}
            />

            {isContextMenuVisible && (
                <div ref={contextMenuRef} className="custom-context-menu">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => handleFormattingOption('bold')}
                        >
                            Bold
                        </Button>

                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => handleFormattingOption('italic')}
                        >
                            Italic
                        </Button>
                    </div>

                    <FormControl variant="outlined" size="small" style={{ minWidth: 180 }}>
                        <InputLabel id="alignment-select-label" style={{ color: 'black' }}>Alignment</InputLabel>
                        <Select
                            labelId="alignment-select-label"
                            defaultValue=""
                            onChange={(e) => handleAlignmentChange(e.target.value as string)}
                            label="Alignment"
                        >
                            <MenuItem value="" disabled>Select Alignment</MenuItem>
                            <MenuItem value="top-left">Top Left</MenuItem>
                            <MenuItem value="top-center">Top Center</MenuItem>
                            <MenuItem value="top-right">Top Right</MenuItem>
                            <MenuItem value="middle-left">Center Left</MenuItem>
                            <MenuItem value="middle-center">Center</MenuItem>
                            <MenuItem value="middle-right">Center Right</MenuItem>
                            <MenuItem value="bottom-left">Bottom Left</MenuItem>
                            <MenuItem value="bottom-center">Bottom Center</MenuItem>
                            <MenuItem value="bottom-right">Bottom Right</MenuItem>
                        </Select>
                    </FormControl>
                </div>
            )}

        </div>
    );
}

const TextEditor: React.FC<Props> = ({ showToolbar, showEditorialTemplates, selectedEditorialType, readOnly, html, onChange, editorStyle }) => {
    const [templates, setTemplates] = useState<EditorialTemplate[] | undefined>(undefined);

    useEffect(() => {
        if (!showEditorialTemplates) {
            setTemplates([]);
            return;
        }
        if (!selectedEditorialType) {
            return;
        }
        getEditorialTemplates(selectedEditorialType).then(response => {
            setTemplates(response.data);
        }).catch(error => {
            console.error(error);
        })
    }, [selectedEditorialType]);

    if (templates === undefined) {
        return <></>
    }

    return (
        <SunEditorWrapper
            showToolbar={showToolbar}
            readOnly={readOnly}
            html={html}
            onChange={onChange}
            templates={templates}
        />
    )
}

export default TextEditor;
